/**
 * Authentication context providing user info, roles, and token acquisition.
 *
 * Roles are resolved by acquiring an access token for the Portal API app
 * (aud: f8862806-...) and parsing the roles/groups claims from the JWT.
 * Falls back to W365LithiumMgmt.Read if no roles are present.
 *
 * @module contexts/AuthContext
 */

import {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useCallback,
  useState,
  useEffect,
} from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { loginRequest, tokenRequest } from '@/config/msalConfig';
import { trackAuthEvent } from '@/services/telemetry';

export type AppRole = 'W365LithiumMgmt.Read' | 'W365LithiumMgmt.ReadWrite';

/** Beta-only role labels used for UX preview mode. Stored in localStorage. */
export type BetaRole =
  | 'W365LithiumMgmt.Read'
  | 'W365LithiumMgmt.ReadWrite'
  | 'W365LithiumMgmt.Admin';

const BETA_ROLE_LEVEL: Record<BetaRole, number> = {
  'W365LithiumMgmt.Read': 1,
  'W365LithiumMgmt.ReadWrite': 2,
  'W365LithiumMgmt.Admin': 3,
};

export const BETA_ROLE_STORAGE_KEY = 'w365a-beta-role';

export interface AuthUser {
  name: string;
  email: string;
  objectId: string;
  initials: string;
  roles: AppRole[];
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  roles: AppRole[];
  hasRole: (role: AppRole) => boolean;
  isAdmin: boolean;
  /** Admin can CRUD volumes (beta role preview aware). */
  canManageVolumes: boolean;
  /** Admin can CRUD policies (beta role preview aware). */
  canManagePolicies: boolean;
  /** Currently active beta role override, or null if using real JWT roles. */
  betaRoleOverride: BetaRole | null;
  /** Set a beta role override for UX preview (persisted to localStorage). Pass null to clear. */
  setBetaRoleOverride: (role: BetaRole | null) => void;
  /** True when the override grants permissions beyond what the real JWT role allows. */
  isMockingRole: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const VALID_ROLES = new Set<string>(['W365LithiumMgmt.Read', 'W365LithiumMgmt.ReadWrite']);

/** Decode the payload of a JWT without verification (client-side claim extraction). */
function parseJwtPayload(token: string): Record<string, unknown> {
  const parts = token.split('.');
  if (parts.length !== 3) return {};
  const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  return JSON.parse(atob(payload));
}

/** Extract roles from a JWT's claims. */
function extractRolesFromToken(token: string): AppRole[] {
  const claims = parseJwtPayload(token);
  if (Array.isArray(claims['roles'])) {
    const tokenRoles = (claims['roles'] as string[]).filter(r => VALID_ROLES.has(r)) as AppRole[];
    if (tokenRoles.length > 0) return tokenRoles;
  }
  return [];
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(p => p.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [tokenRoles, setTokenRoles] = useState<AppRole[] | null>(null);
  const [rolesFetched, setRolesFetched] = useState(false);
  const [betaRoleOverride, setBetaRoleOverrideState] = useState<BetaRole | null>(() => {
    const stored = localStorage.getItem(BETA_ROLE_STORAGE_KEY);
    return (stored as BetaRole) || null;
  });

  const setBetaRoleOverride = useCallback((role: BetaRole | null) => {
    setBetaRoleOverrideState(role);
    if (role) {
      localStorage.setItem(BETA_ROLE_STORAGE_KEY, role);
    } else {
      localStorage.removeItem(BETA_ROLE_STORAGE_KEY);
    }
  }, []);

  const account = accounts[0] ?? null;
  const msalLoading = inProgress !== InteractionStatus.None;

  // Acquire access token helper (exposed to consumers)
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!account) return null;
    try {
      const response = await instance.acquireTokenSilent({ ...tokenRequest, account });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect(tokenRequest);
        return null;
      }
      console.error('[Auth] Token acquisition failed:', error);
      return null;
    }
  }, [instance, account]);

  // Acquire Portal API token and extract roles from the JWT claims
  useEffect(() => {
    if (!isAuthenticated || !account || msalLoading || rolesFetched) return;

    let cancelled = false;

    (async () => {
      try {
        const token = await getAccessToken();
        if (cancelled || !token) {
          setRolesFetched(true);
          return;
        }

        const roles = extractRolesFromToken(token);
        console.log('[Auth] Roles from Portal API token:', roles);
        if (roles.length > 0) {
          setTokenRoles(roles);
        }
        trackAuthEvent('login_success');
      } catch (error) {
        console.warn('[Auth] Failed to acquire Portal API token for role resolution:', error);
        trackAuthEvent('login_fail');
      } finally {
        if (!cancelled) setRolesFetched(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, account, msalLoading, rolesFetched, getAccessToken]);

  // Reset on logout
  useEffect(() => {
    if (!isAuthenticated) {
      setTokenRoles(null);
      setRolesFetched(false);
    }
  }, [isAuthenticated]);

  // Use roles from Portal API token, fall back to Read
  const roles = tokenRoles ?? (rolesFetched ? ['W365LithiumMgmt.Read' as AppRole] : []);

  const user = useMemo<AuthUser | null>(() => {
    if (!account) return null;
    return {
      name: account.name || 'Unknown User',
      email: account.username || '',
      objectId: account.localAccountId || '',
      initials: getInitials(account.name || 'U'),
      roles,
    };
  }, [account, roles]);

  const hasRole = useCallback((role: AppRole) => roles.includes(role), [roles]);
  const jwtIsAdmin = useMemo(() => roles.includes('W365LithiumMgmt.ReadWrite'), [roles]);
  const jwtRoleLevel = jwtIsAdmin ? BETA_ROLE_LEVEL['W365LithiumMgmt.ReadWrite'] : BETA_ROLE_LEVEL['W365LithiumMgmt.Read'];

  // Effective role level — override wins over JWT when set.
  const effectiveLevel = betaRoleOverride ? BETA_ROLE_LEVEL[betaRoleOverride] : jwtRoleLevel;

  // Permissions derived consistently from the effective level regardless of override.
  const isAdmin = effectiveLevel >= BETA_ROLE_LEVEL['W365LithiumMgmt.ReadWrite'];
  const canManageVolumes = effectiveLevel >= BETA_ROLE_LEVEL['W365LithiumMgmt.Admin'];
  const canManagePolicies = effectiveLevel >= BETA_ROLE_LEVEL['W365LithiumMgmt.Admin'];
  const isMockingRole = betaRoleOverride !== null && BETA_ROLE_LEVEL[betaRoleOverride] > jwtRoleLevel;

  const login = useCallback(async () => {
    if (inProgress !== InteractionStatus.None) return;
    await instance.loginRedirect(loginRequest);
  }, [instance, inProgress]);

  const logout = useCallback(async () => {
    trackAuthEvent('logout');
    await instance.logoutRedirect({ postLogoutRedirectUri: `${window.location.origin}/signin` });
  }, [instance]);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      isAuthenticated,
      isLoading: msalLoading || (isAuthenticated && !rolesFetched),
      roles,
      hasRole,
      isAdmin,
      canManageVolumes,
      canManagePolicies,
      betaRoleOverride,
      setBetaRoleOverride,
      isMockingRole,
      login,
      logout,
      getAccessToken,
    }),
    [
      user,
      isAuthenticated,
      msalLoading,
      rolesFetched,
      roles,
      hasRole,
      isAdmin,
      canManageVolumes,
      canManagePolicies,
      betaRoleOverride,
      setBetaRoleOverride,
      isMockingRole,
      login,
      logout,
      getAccessToken,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
